import { useSeo } from "@/lib/seo";
import { Overline, Reveal, Section } from "@/components/shared";
import { PageHero, FAQ, StepList } from "@/components/PageParts";
import { PricingCard } from "@/components/PricingCard";
import { FunctionSquare, LineChart, MessagesSquare } from "lucide-react";

const provide = [
  { icon: FunctionSquare, title: "Custom Mathematical Models", desc: "Logical, differential, stochastic or agent-based models built around your research question." },
  { icon: LineChart, title: "Simulations & Scenario Analysis", desc: "Perturbations, interventions and scenario testing to explore possible behaviors." },
  { icon: MessagesSquare, title: "Research Consulting", desc: "Methodological advice, model interpretation and systems-level framing." },
];

const methods = [
  "Logical / Boolean networks", "Differential-equation models", "Stochastic models",
  "Agent-based models", "Network analysis", "Sensitivity analysis",
  "Scenario testing", "Data-informed modeling", "Hypothesis-driven modeling",
];

const fields = [
  "Biology and medicine", "Physics and chemistry", "Engineering and technology",
  "Economics and finance", "Social and political sciences", "Environmental sciences", "Multidisciplinary teams",
];

const howWeWork = [
  { t: "Understand" }, { t: "Formalize" }, { t: "Model" },
  { t: "Simulate" }, { t: "Interpret" }, { t: "Refine" },
];

const included = [
  "One active project at a time",
  "Mathematical / computational model development",
  "Simulation and analysis",
  "Reports with interpretations",
  "Research consultation",
  "Methodological advice",
  "Clear monthly priorities",
];

const faqGroups = [
  { q: "How do we start a project?", a: "We begin with a research consultation to understand the question, then formalize it into a modeling plan with clear monthly priorities." },
  { q: "What data and methods do you use?", a: "Depending on the problem: logical/Boolean networks, differential equations, stochastic and agent-based models, network and sensitivity analysis, and data-informed modeling." },
  { q: "What are the deliverables?", a: "Working models, simulations and scenario analyses, plus reports with interpretations and recommended next steps." },
  { q: "Who owns the work and how is confidentiality handled?", a: "You retain ownership of project-specific models and results. Confidentiality is respected throughout the engagement." },
  { q: "How does collaboration work?", a: "We work closely and iteratively with your team, refining models as understanding and data evolve." },
];

export default function ForResearchTeams() {
  useSeo(
    "Mathematical Modeling for Research Teams | Systems Beyond",
    "Systems Beyond helps academic and private research teams build mathematical and computational models, simulations and systems-based research strategies."
  );

  return (
    <div>
      <PageHero
        overline="For Research Teams"
        title="Mathematical and computational modeling for research teams."
        subtitle="We help research teams turn complex questions, mechanisms, hypotheses and data into models that can be simulated, tested, interpreted and refined."
      />

      <Section className="py-16 lg:py-24" grid>
        <div className="max-w-3xl">
          <Overline>The challenge</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">
            For research questions that are too connected for linear thinking.
          </h2>
          <p className="mt-6 text-dark-gray leading-relaxed">
            Many research problems involve feedback loops, nonlinear responses, thresholds,
            uncertainty, multi-scale interactions and incomplete information. We help make that
            structure explicit and testable.
          </p>
        </div>
      </Section>

      <Section className="pb-16 lg:pb-24">
        <Overline>What we provide</Overline>
        <div className="mt-10 grid md:grid-cols-3 gap-px bg-silver/40 border border-silver/40">
          {provide.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <div className="bg-white p-8 h-full group hover:bg-off-white transition-colors">
                <div className="w-11 h-11 border border-silver flex items-center justify-center group-hover:border-acid transition-colors">
                  <p.icon size={18} strokeWidth={1.5} className="text-ink" />
                </div>
                <h3 className="mt-6 text-xl text-ink">{p.title}</h3>
                <p className="mt-3 text-sm text-dark-gray leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section dark className="py-20 lg:py-28" grid>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <Overline dark>Possible methods</Overline>
            <div className="mt-8 flex flex-wrap gap-2">
              {methods.map((m) => (
                <span key={m} className="font-mono-sci text-xs text-white/70 border border-white/15 px-4 py-2.5 hover:border-acid hover:text-white transition-colors">{m}</span>
              ))}
            </div>
          </div>
          <div>
            <Overline dark>Who we work with</Overline>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="w-1.5 h-1.5 bg-acid rounded-full" />{f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section className="py-20 lg:py-28">
        <Overline>How we work</Overline>
        <div className="mt-10"><StepList steps={howWeWork} /></div>
      </Section>

      {/* Pricing */}
      <Section className="pb-20 lg:pb-28">
        <div className="max-w-2xl mb-10">
          <Overline>Ongoing modeling support</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">A focused monthly retainer.</h2>
        </div>
        <PricingCard
          overline="RESEARCH RETAINER"
          title="Ongoing modeling support"
          price="$2,500"
          planId="research_teams"
          consultCta="Book a Research Consultation"
          includes={included}
          exclusion="External software, special data acquisition, paid APIs, lab work and third-party services are not included."
        />
      </Section>

      <Section className="pb-20 lg:pb-28">
        <div className="max-w-2xl mb-10">
          <Overline>FAQ</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">Working with research teams.</h2>
        </div>
        <FAQ items={faqGroups} />
      </Section>
    </div>
  );
}
